import i18n from '@/i18n';
import { getCurrencySymbol, resolveCurrency } from '@/utils/currency';

export interface SupplyAgentRuleRecord {
  id?: number;
  rule_type?: string;
  name?: string;
  value?: Record<string, unknown>;
  engine?: string;
  scope?: string;
  unit?: string;
  currency?: string;
  is_active?: boolean;
  applies_when?: Record<string, unknown>;
  scope_ref?: Record<string, unknown>;
  original_instruction?: string;
  completion_strategy_choice?: string;
}

export function normalizeSupplyAgentRules(
  rules: unknown,
): SupplyAgentRuleRecord[] {
  return Array.isArray(rules) ? (rules as SupplyAgentRuleRecord[]) : [];
}

export const COMPLETION_RULE_TYPES = new Set([
  'minimum_order_value',
  'target_order_value',
  'minimum_order_units',
]);

/** Topes €/uds: eligen cómo recortar, no cómo completar. */
export const REDUCTION_RULE_TYPES = new Set([
  'maximum_order_value',
  'budget_limit',
  'maximum_order_units',
]);

export type SupplyAgentCompletionStrategy =
  | 'coverage_layers'
  | 'top_sales'
  | 'proportional'
  | 'high_margin';

export type SupplyAgentReductionStrategy = 'slow_movers' | 'proportional';

export const COMPLETION_STRATEGY_OPTIONS: {
  id: SupplyAgentCompletionStrategy;
  labelKey: string;
}[] = [
  { id: 'coverage_layers', labelKey: 'supply.supplyAgent.completionStrategyCoverage' },
  { id: 'top_sales', labelKey: 'supply.supplyAgent.completionStrategyTopSales' },
  { id: 'proportional', labelKey: 'supply.supplyAgent.completionStrategyProportional' },
  { id: 'high_margin', labelKey: 'supply.supplyAgent.completionStrategyHighMargin' },
];

export const REDUCTION_STRATEGY_OPTIONS: {
  id: SupplyAgentReductionStrategy;
  labelKey: string;
}[] = [
  { id: 'slow_movers', labelKey: 'supply.supplyAgent.reductionStrategySlowMovers' },
  { id: 'proportional', labelKey: 'supply.supplyAgent.reductionStrategyProportional' },
];

export function isCompletionRule(rule: { rule_type?: string } | null | undefined): boolean {
  return COMPLETION_RULE_TYPES.has(String(rule?.rule_type || '').toLowerCase());
}

export function isReductionRule(rule: { rule_type?: string } | null | undefined): boolean {
  return REDUCTION_RULE_TYPES.has(String(rule?.rule_type || '').toLowerCase());
}

export function completionStrategyKey(rule: {
  id?: number;
  rule_type?: string;
}): string | null {
  if (!isCompletionRule(rule)) return null;
  if (rule.id != null) return `saved:${rule.id}`;
  return `session:${String(rule.rule_type).toLowerCase()}`;
}

export function reductionStrategyKey(rule: {
  id?: number;
  rule_type?: string;
}): string | null {
  if (!isReductionRule(rule)) return null;
  if (rule.id != null) return `saved:${rule.id}`;
  return `session:${String(rule.rule_type).toLowerCase()}`;
}

export function readCompletionStrategy(
  rule: { value?: Record<string, unknown>; applies_when?: Record<string, unknown>; completion_strategy_choice?: unknown },
): SupplyAgentCompletionStrategy | null {
  const value = rule.value || {};
  const applies = rule.applies_when || {};
  const raw =
    value.completion_strategy ||
    value.strategy ||
    rule.completion_strategy_choice ||
    applies.strategy;
  const key = String(raw || '').trim().toLowerCase();
  if (
    key === 'coverage_layers' ||
    key === 'top_sales' ||
    key === 'proportional' ||
    key === 'high_margin'
  ) {
    return key;
  }
  return null;
}

export function readReductionStrategy(
  rule: { value?: Record<string, unknown>; applies_when?: Record<string, unknown> },
): SupplyAgentReductionStrategy | null {
  const value = rule.value || {};
  const applies = rule.applies_when || {};
  const raw = value.reduction_strategy || value.strategy || applies.strategy;
  const key = String(raw || '').trim().toLowerCase();
  if (key === 'slow_movers' || key === 'least_urgent') return 'slow_movers';
  if (key === 'proportional') return 'proportional';
  return null;
}

export function withCompletionStrategy(
  rule: Record<string, unknown>,
  strategy: SupplyAgentCompletionStrategy,
): Record<string, unknown> {
  const prevValue =
    rule.value && typeof rule.value === 'object' && !Array.isArray(rule.value)
      ? (rule.value as Record<string, unknown>)
      : {};
  const prevApplies =
    rule.applies_when && typeof rule.applies_when === 'object' && !Array.isArray(rule.applies_when)
      ? (rule.applies_when as Record<string, unknown>)
      : {};
  return {
    ...rule,
    value: { ...prevValue, completion_strategy: strategy, strategy },
    applies_when: { ...prevApplies, strategy },
    completion_strategy_choice: strategy,
  };
}

export function withReductionStrategy(
  rule: Record<string, unknown>,
  strategy: SupplyAgentReductionStrategy,
): Record<string, unknown> {
  const prevValue =
    rule.value && typeof rule.value === 'object' && !Array.isArray(rule.value)
      ? (rule.value as Record<string, unknown>)
      : {};
  const prevApplies =
    rule.applies_when && typeof rule.applies_when === 'object' && !Array.isArray(rule.applies_when)
      ? (rule.applies_when as Record<string, unknown>)
      : {};
  return {
    ...rule,
    value: { ...prevValue, reduction_strategy: strategy, strategy },
    applies_when: { ...prevApplies, strategy },
  };
}

export function completionStrategyOverlay(
  ruleType: string,
  strategy: SupplyAgentCompletionStrategy,
): Record<string, unknown> {
  return {
    rule_type: ruleType,
    engine: 'soft_optimization',
    /** Solo transporta estrategia; no es una regla de umbral (no debe listarse como aplicada). */
    is_completion_strategy_overlay: true,
    is_strategy_overlay: true,
    value: { completion_strategy: strategy, strategy },
    applies_when: { strategy },
  };
}

export function reductionStrategyOverlay(
  ruleType: string,
  strategy: SupplyAgentReductionStrategy,
): Record<string, unknown> {
  return {
    rule_type: ruleType,
    engine: 'soft_optimization',
    is_reduction_strategy_overlay: true,
    is_strategy_overlay: true,
    value: { reduction_strategy: strategy, strategy },
    applies_when: { strategy },
  };
}

function t(key: string, opts?: Record<string, unknown>): string {
  return i18n.t(`supply.supplyAgent.${key}`, opts);
}

function localeTag(): string {
  return (i18n.language || 'es').toLowerCase().startsWith('it') ? 'it-IT' : 'es-ES';
}

function fmtNum(value: number): string {
  return Number(value).toLocaleString(localeTag());
}

function moneySymbol(rule: SupplyAgentRuleRecord): string {
  const fromValue =
    rule.value && typeof rule.value.currency === 'string'
      ? rule.value.currency
      : undefined;
  const code = resolveCurrency(rule.currency || fromValue || rule.unit);
  return getCurrencySymbol(code, localeTag());
}

function monetaryScopeHint(rule: SupplyAgentRuleRecord): string {
  if (rule.scope === 'supplier' || rule.scope_ref?.supplier_id != null) {
    return t('ruleScopeSupplierOnly');
  }
  if (rule.scope === 'warehouse' || rule.scope_ref?.warehouse_id != null) {
    return t('ruleScopeWarehouseShared');
  }
  return '';
}

function familyScopeHint(rule: SupplyAgentRuleRecord): string {
  if (rule.scope === 'supplier' || rule.scope_ref?.supplier_id != null) {
    return t('ruleScopeSupplierOnly');
  }
  return t('ruleScopeWarehouseAll');
}

function distributionHint(rule: SupplyAgentRuleRecord): string {
  const value = rule.value || {};
  const scope = value.distribution_scope;
  if (scope === 'abc') {
    const cats = Array.isArray(value.abc_categories)
      ? (value.abc_categories as string[]).join(', ')
      : '';
    return cats ? t('ruleDistAbc', { cats }) : '';
  }
  if (scope === 'all') {
    return t('ruleDistAll');
  }
  return '';
}

export function formatSupplyAgentRuleLabel(rule: SupplyAgentRuleRecord): string {
  const type = String(rule.rule_type || '');
  const value = rule.value || {};
  const amount = value.amount;
  const units = value.units ?? amount;
  const families = Array.isArray(value.family_names)
    ? (value.family_names as string[]).join(', ')
    : '';
  const keywords = Array.isArray(value.keywords)
    ? (value.keywords as string[]).join(', ')
    : Array.isArray(value.terms)
      ? (value.terms as string[]).join(', ')
      : '';
  const productKeywords = Array.isArray(value.product_keywords)
    ? (value.product_keywords as string[]).join(', ')
    : '';

  switch (type) {
    case 'minimum_order_value':
      return amount != null
        ? t('ruleMinEuro', { amount: fmtNum(Number(amount)), symbol: moneySymbol(rule) }) +
            monetaryScopeHint(rule)
        : t('ruleMinOrder') + monetaryScopeHint(rule);
    case 'maximum_order_value':
      return amount != null
        ? t('ruleMaxEuro', { amount: fmtNum(Number(amount)), symbol: moneySymbol(rule) }) +
            monetaryScopeHint(rule)
        : t('ruleMaxOrder') + monetaryScopeHint(rule);
    case 'budget_limit':
      return amount != null
        ? t('ruleBudgetEuro', { amount: fmtNum(Number(amount)), symbol: moneySymbol(rule) }) +
            monetaryScopeHint(rule)
        : t('ruleBudget') + monetaryScopeHint(rule);
    case 'target_order_value':
      return amount != null
        ? t('ruleTargetEuro', { amount: fmtNum(Number(amount)), symbol: moneySymbol(rule) }) +
            monetaryScopeHint(rule)
        : t('ruleTarget') + monetaryScopeHint(rule);
    case 'maximum_order_units':
      return units != null
        ? t('ruleMaxUnits', { units: fmtNum(Number(units)) }) + monetaryScopeHint(rule)
        : t('ruleMaxUnitsPlain') + monetaryScopeHint(rule);
    case 'minimum_order_units':
      return units != null
        ? t('ruleMinUnits', { units: fmtNum(Number(units)) }) + monetaryScopeHint(rule)
        : t('ruleMinUnitsPlain') + monetaryScopeHint(rule);
    case 'sku_min_order_quantity':
      return units != null
        ? t('ruleSkuMin', {
            units: fmtNum(Number(units)),
            product: productKeywords || '?',
          })
        : t('ruleSkuMinPlain');
    case 'sku_max_order_quantity':
      return units != null
        ? families
          ? t('ruleSkuMaxFamily', {
              units: fmtNum(Number(units)),
              family: families,
            })
          : t('ruleSkuMax', { units: fmtNum(Number(units)) })
        : t('ruleSkuMaxPlain');
    case 'family_minimum_quantity': {
      const scopeHint = familyScopeHint(rule);
      const distHint = distributionHint(rule);
      return units != null
        ? t('ruleFamilyMinUnits', {
            family: families || '?',
            units: fmtNum(Number(units)),
          }) +
            scopeHint +
            distHint
        : t('ruleFamilyMinPlain') + scopeHint + distHint;
    }
    case 'family_minimum_order_value': {
      const scopeHint = familyScopeHint(rule);
      const distHint = distributionHint(rule);
      return amount != null
        ? t('ruleFamilyMinEuro', {
            family: families || '?',
            amount: fmtNum(Number(amount)),
            symbol: moneySymbol(rule),
          }) +
            scopeHint +
            distHint
        : t('ruleFamilyMinEuroPlain') + scopeHint + distHint;
    }
    case 'family_maximum_quantity': {
      const scopeHint = familyScopeHint(rule);
      const distHint = distributionHint(rule);
      return units != null
        ? t('ruleFamilyMaxUnits', {
            family: families || '?',
            units: fmtNum(Number(units)),
          }) +
            scopeHint +
            distHint
        : t('ruleFamilyMaxPlain') + scopeHint + distHint;
    }
    case 'include_only_product_keywords':
      return keywords
        ? t('ruleFilterKeywords', { keywords }) + familyScopeHint(rule)
        : t('ruleFilterKeywordsPlain') + familyScopeHint(rule);
    case 'include_only_families':
      return families
        ? t('ruleFilterFamily', { family: families }) + familyScopeHint(rule)
        : t('ruleFilterFamilyPlain') + familyScopeHint(rule);
    case 'complete_order_with_fast_movers':
      return t('ruleCompleteFast');
    case 'urgent_order_mode':
      return t('ruleUrgent') + monetaryScopeHint(rule);
    case 'commercial_commitment_target': {
      const year = value.year != null ? ` ${value.year}` : '';
      if (units != null) {
        return t('ruleCommitmentUnits', {
          units: fmtNum(Number(units)),
          year,
        });
      }
      if (amount != null) {
        return t('ruleCommitmentEuro', {
          amount: fmtNum(Number(amount)),
          year,
          symbol: moneySymbol(rule),
        });
      }
      return t('ruleCommitmentPlain');
    }
    case 'complete_order_with_high_margin_items':
      return t('ruleCompleteMargin');
    case 'reduce_slow_movers':
    case 'remove_least_urgent_items':
      return t('ruleCutSlow');
    case 'exclude_from_order_completion':
      return t('ruleExcludeFresh');
    case 'sku_order_multiple':
    case 'sku_box_quantity':
      return value.multiple != null
        ? t('ruleMultiplesOf', { multiple: value.multiple })
        : t('ruleBoxMultiple');
    default:
      return String(rule.name || type || t('ruleFallback'));
  }
}

/** Etiqueta corta por tipo de regla (para columna Motivo / reglas aplicadas). */
const RULE_TYPE_SHORT_KEYS: Record<string, string> = {
  include_only_product_keywords: 'ruleTypeShort.include_only_product_keywords',
  include_only_families: 'ruleTypeShort.include_only_families',
  exclude_zero_unit_price: 'ruleTypeShort.exclude_zero_unit_price',
  exclude_from_order_completion: 'ruleTypeShort.exclude_from_order_completion',
  avoid_perishable_items_for_completion: 'ruleTypeShort.avoid_perishable_items_for_completion',
  sku_box_quantity: 'ruleTypeShort.sku_box_quantity',
  sku_order_multiple: 'ruleTypeShort.sku_order_multiple',
  sku_max_order_quantity: 'ruleTypeShort.sku_max_order_quantity',
  sku_min_order_quantity: 'ruleTypeShort.sku_min_order_quantity',
  fixed_order_quantity: 'ruleTypeShort.fixed_order_quantity',
  family_minimum_quantity: 'ruleTypeShort.family_minimum_quantity',
  family_minimum_order_value: 'ruleTypeShort.family_minimum_order_value',
  family_maximum_quantity: 'ruleTypeShort.family_maximum_quantity',
  minimum_order_value: 'ruleTypeShort.minimum_order_value',
  maximum_order_value: 'ruleTypeShort.maximum_order_value',
  budget_limit: 'ruleTypeShort.budget_limit',
  target_order_value: 'ruleTypeShort.target_order_value',
  minimum_order_units: 'ruleTypeShort.minimum_order_units',
  maximum_order_units: 'ruleTypeShort.maximum_order_units',
  complete_order_with_fast_movers: 'ruleTypeShort.complete_order_with_fast_movers',
  complete_order_with_high_margin_items: 'ruleTypeShort.complete_order_with_high_margin_items',
  complete_order_with_non_perishables: 'ruleTypeShort.complete_order_with_non_perishables',
  remove_least_urgent_items: 'ruleTypeShort.remove_least_urgent_items',
  reduce_slow_movers: 'ruleTypeShort.reduce_slow_movers',
  round_to_pallet: 'ruleTypeShort.round_to_pallet',
  sku_layer_quantity: 'ruleTypeShort.sku_layer_quantity',
  urgent_order_mode: 'ruleTypeShort.urgent_order_mode',
  commercial_commitment_target: 'ruleTypeShort.commercial_commitment_target',
};

export function shortRuleTypeLabel(ruleType: string | undefined | null): string {
  const type = String(ruleType || '').toLowerCase();
  const key = RULE_TYPE_SHORT_KEYS[type];
  if (key) return t(key);
  return t('ruleFallback');
}

/** Lista corta y deduplicada de reglas aplicadas a una línea (columna Motivo). */
export function formatAppliedRulesShort(
  reasons: Array<{ label?: string; rule_type?: string; rule_id?: number }> | undefined,
): string {
  if (!reasons?.length) return '';
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const reason of reasons) {
    const key =
      reason.rule_id != null
        ? `id:${reason.rule_id}`
        : `type:${String(reason.rule_type || reason.label || '').toLowerCase()}`;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const short = reason.rule_type
      ? shortRuleTypeLabel(reason.rule_type)
      : String(reason.label || '').trim();
    if (short) labels.push(short);
  }
  return labels.join(' · ');
}
